import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AlertModal } from '../components/AlertModal';
import { ConfirmModal } from '../components/ConfirmModal';
import { useUrlState } from '../hooks/useUrlState';
import { apiClient } from '../lib/api-client';

type Tier = {
  id: string;
  key: string;
  name: string;
  displayName: Record<string, string>;
  description: Record<string, string> | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

const statusStyles: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  inactive: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
};

function TiersPage() {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 表示条件は URL クエリで管理し、戻る / 進む と URL 共有に対応する
  const [{ includeInactive }, setUrlState] = useUrlState({
    includeInactive: false as boolean
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'default' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant?: 'info' | 'success' | 'warning' | 'error';
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  const fetchTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getTiers({ includeInactive });
      setTiers(response.tiers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
  }, [includeInactive]);

  const handleDelete = (tier: Tier) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Tier',
      message: `Tier "${tier.key}" を削除しますか？\nこの操作は取り消せません。`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        try {
          await apiClient.deleteTier(tier.id);
          await fetchTiers();
        } catch (err) {
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete tier',
            variant: 'error'
          });
        }
      }
    });
  };

  const handleToggleActive = async (tier: Tier) => {
    try {
      await apiClient.updateTier(tier.id, { isActive: !tier.isActive });
      await fetchTiers();
    } catch (err) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to update tier',
        variant: 'error'
      });
    }
  };

  if (loading && tiers.length === 0) {
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <div className='text-lg text-gray-600 dark:text-gray-300'>Loading tiers...</div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Tier Management</h1>
        <Link
          to='/tiers/create'
          className='rounded-md bg-blue-600 px-4 py-2 text-center text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
        >
          Create New Tier
        </Link>
      </div>

      <div className='flex items-center gap-4'>
        <label className='flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300'>
          <input
            type='checkbox'
            checked={includeInactive}
            onChange={e => setUrlState({ includeInactive: e.target.checked })}
            className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
          />
          Show inactive tiers
        </label>
        {loading && <span className='text-sm text-gray-500 dark:text-gray-400'>Refreshing...</span>}
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <p className='text-red-800 dark:text-red-200'>Error: {error}</p>
          <button
            onClick={fetchTiers}
            className='mt-2 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700'
          >
            Retry
          </button>
        </div>
      )}

      {/* Desktop Table */}
      <div className='hidden rounded-lg bg-white shadow-md md:block dark:bg-gray-800'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
            <thead className='bg-gray-50 dark:bg-gray-700'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Key
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Name
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Display Name (EN)
                </th>
                <th className='px-6 py-3 text-center text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Order
                </th>
                <th className='px-6 py-3 text-center text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Status
                </th>
                <th className='px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-300'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800'>
              {tiers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className='px-6 py-12 text-center text-gray-500 dark:text-gray-400'
                  >
                    No tiers found
                  </td>
                </tr>
              ) : (
                tiers.map(tier => (
                  <tr key={tier.id} className='hover:bg-gray-50 dark:hover:bg-gray-700/50'>
                    <td className='px-6 py-4 font-mono text-sm font-bold whitespace-nowrap text-gray-900 dark:text-white'>
                      {tier.key}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-900 dark:text-white'>
                      {tier.name}
                    </td>
                    <td className='px-6 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400'>
                      {tier.displayName?.en ?? '-'}
                    </td>
                    <td className='px-6 py-4 text-center text-sm whitespace-nowrap text-gray-900 dark:text-white'>
                      {tier.displayOrder}
                    </td>
                    <td className='px-6 py-4 text-center whitespace-nowrap'>
                      <span
                        className={`inline-flex rounded-full px-2 text-xs leading-5 font-semibold ${
                          tier.isActive ? statusStyles.active : statusStyles.inactive
                        }`}
                      >
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className='px-6 py-4 text-right text-sm font-medium whitespace-nowrap'>
                      <div className='flex justify-end gap-2'>
                        <Link
                          to={`/tiers/${tier.id}/edit`}
                          className='rounded bg-gray-100 px-3 py-1 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleToggleActive(tier)}
                          className={`rounded px-3 py-1 ${
                            tier.isActive
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50'
                              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50'
                          }`}
                        >
                          {tier.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(tier)}
                          className='rounded bg-red-100 px-3 py-1 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50'
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className='space-y-4 md:hidden'>
        {tiers.length === 0 ? (
          <div className='rounded-lg bg-white p-6 text-center text-gray-500 shadow-md dark:bg-gray-800 dark:text-gray-400'>
            No tiers found
          </div>
        ) : (
          tiers.map(tier => (
            <div key={tier.id} className='rounded-lg bg-white p-4 shadow-md dark:bg-gray-800'>
              <div className='mb-3 flex items-start justify-between'>
                <div>
                  <div className='font-mono text-base font-bold text-gray-900 dark:text-white'>
                    {tier.key}
                  </div>
                  <div className='text-sm text-gray-500 dark:text-gray-400'>{tier.name}</div>
                </div>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    tier.isActive ? statusStyles.active : statusStyles.inactive
                  }`}
                >
                  {tier.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className='space-y-2 border-t border-gray-100 pt-3 text-sm dark:border-gray-700'>
                <div className='flex justify-between'>
                  <span className='text-gray-500 dark:text-gray-400'>Display (EN):</span>
                  <span className='text-gray-900 dark:text-white'>
                    {tier.displayName?.en ?? '-'}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-gray-500 dark:text-gray-400'>Order:</span>
                  <span className='text-gray-900 dark:text-white'>{tier.displayOrder}</span>
                </div>
              </div>

              <div className='mt-4 flex gap-2'>
                <Link
                  to={`/tiers/${tier.id}/edit`}
                  className='flex-1 rounded-md bg-gray-100 py-2 text-center text-sm font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                >
                  Edit
                </Link>
                <button
                  onClick={() => handleToggleActive(tier)}
                  className={`flex-1 rounded-md py-2 text-sm font-medium ${
                    tier.isActive
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  }`}
                >
                  {tier.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(tier)}
                  className='rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300'
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() =>
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
        }
      />
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ isOpen: false, title: '', message: '' })}
      />
    </div>
  );
}

export default TiersPage;
